import { useEffect, useState, useCallback } from 'react';
import {
  CodeQuizGameState,
  CodeQuizMove,
  GameInstance,
  GameMovePayload,
  GameUpdatePayload,
  FakeSOSocket,
} from '../types/types';
import { getGameById, joinGame, leaveGame } from '../services/gamesService';
import useUserContext from './useUserContext';

/**
 * Custom hook to manage the state and logic for a CodeQuiz game
 *
 * @param gameId - The ID of the game to join/manage
 * @param playerId - The ID of the current player (usually username)
 * @returns Game state and functions to interact with the game
 */
const useCodeQuizGame = (gameId: string, playerId: string) => {
  const [game, setGame] = useState<GameInstance<CodeQuizGameState> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<number>(30000); // Default 30 seconds
  const [timePercentage, setTimePercentage] = useState<number>(100);
  const [socket, setSocket] = useState<FakeSOSocket | null>(null);

  // Get socket from context - outside of useEffect
  let userContextSocket: FakeSOSocket | null = null;
  try {
    const context = useUserContext();
    userContextSocket = context?.socket || null;
  } catch (err) {
    // Handle error silently; set the error state in the useEffect
  }

  useEffect(() => {
    if (userContextSocket) {
      setSocket(userContextSocket);
    } else {
      setError('Socket connection not available. Please refresh the page or log in again.');
    }
  }, [userContextSocket]);

  const initializeTimer = useCallback((currentGame: GameInstance<CodeQuizGameState>) => {
    if (currentGame.state.status !== 'IN_PROGRESS') return;

    const { timePerQuestion, currentQuestionStartTime } = currentGame.state;

    if (currentQuestionStartTime) {
      const elapsed = Date.now() - currentQuestionStartTime;
      const remaining = Math.max(0, timePerQuestion - elapsed);

      setTimeLeft(remaining);
      setTimePercentage((remaining / timePerQuestion) * 100);
    } else {
      setTimeLeft(timePerQuestion);
      setTimePercentage(100);
    }
  }, []);

  const fetchGame = useCallback(async () => {
    try {
      if (!gameId) return;

      const fetchedGame = await getGameById(gameId);

      setGame(fetchedGame as GameInstance<CodeQuizGameState>);

      if (fetchedGame.state.status === 'IN_PROGRESS') {
        initializeTimer(fetchedGame as GameInstance<CodeQuizGameState>);
      }
    } catch (fetchError) {
      setError('Error fetching game details');
    }
  }, [gameId, initializeTimer]);

  const joinGameHandler = useCallback(async () => {
    try {
      if (!gameId || !playerId) return;

      try {
        const gameInfo = await getGameById(gameId);

        if (gameInfo.state.status === 'OVER') {
          setGame(gameInfo as GameInstance<CodeQuizGameState>);
          return;
        }
      } catch (checkError) {
        // Continue with join attempt if we can't verify status
      }

      await joinGame(gameId, playerId);
      await fetchGame();
      socket?.emit('joinGame', gameId);
    } catch (joinError) {
      // Special handling for games that might not exist or have ended
      if (joinError instanceof Error && joinError.message.includes('500')) {
        try {
          const gameInfo = await getGameById(gameId);
          setGame(gameInfo as GameInstance<CodeQuizGameState>);
          if (gameInfo.state.status === 'OVER') {
            setError('This game has already ended. You can view the results but cannot join.');
          } else {
            setError('Error joining the game');
          }
        } catch (fetchError) {
          setError('This game may no longer exist or has already ended.');
        }
      } else {
        setError('Error joining the game');
      }
    }
  }, [gameId, playerId, socket, fetchGame]);

  const leaveGameHandler = useCallback(async () => {
    try {
      if (!gameId || !playerId) return;

      await leaveGame(gameId, playerId);
      socket?.emit('leaveGame', gameId);
    } catch (leaveError) {
      setError('Error leaving the game');
    }
  }, [gameId, playerId, socket]);

  const submitAnswer = useCallback(
    (questionId: string, selectedOption: number, timeToAnswer: number) => {
      if (!gameId || !playerId || !socket) {
        return;
      }

      const movePayload: GameMovePayload = {
        gameID: gameId,
        move: {
          playerID: playerId,
          gameID: gameId,
          move: {
            questionId,
            selectedOption,
            timeToAnswer,
          } as CodeQuizMove,
        },
      };

      socket.emit('makeMove', movePayload);
    },
    [gameId, playerId, socket],
  );

  useEffect(() => {
    if (!game || game.state.status !== 'IN_PROGRESS') return undefined;

    const interval = setInterval(() => {
      if (timeLeft <= 0) {
        clearInterval(interval);
        return;
      }

      const newTimeLeft = Math.max(0, timeLeft - 1000);
      setTimeLeft(newTimeLeft);
      setTimePercentage((newTimeLeft / game.state.timePerQuestion) * 100);
    }, 1000);

    return () => clearInterval(interval);
  }, [game, timeLeft]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleGameUpdate = (data: GameUpdatePayload) => {
      const updatedGame = data.gameInstance as GameInstance<CodeQuizGameState>;
      setGame(updatedGame);

      if (updatedGame.state.status === 'IN_PROGRESS') {
        initializeTimer(updatedGame);
      }
    };

    const handleGameError = (data: { player: string; error: string }) => {
      if (data.player === playerId) {
        setError(data.error);
      }
    };

    socket.on('gameUpdate', handleGameUpdate);
    socket.on('gameError', handleGameError);

    return () => {
      socket.off('gameUpdate', handleGameUpdate);
      socket.off('gameError', handleGameError);
    };
  }, [socket, playerId, initializeTimer]);

  useEffect(() => {
    if (gameId) {
      fetchGame();

      const pollInterval = setInterval(() => {
        if (game?.state.status === 'IN_PROGRESS') {
          fetchGame();
        }
      }, 3000);

      return () => clearInterval(pollInterval);
    }
    return undefined;
  }, [gameId, fetchGame, game?.state.status]);

  return {
    game,
    error,
    joinGame: joinGameHandler,
    leaveGame: leaveGameHandler,
    submitAnswer,
    timeLeft,
    timePercentage,
  };
};

export default useCodeQuizGame;
